import { alphabeticalSorter } from "@suse-o11y/alphabetical-sorter";
import { filter } from "@suse-o11y/filter";
import { flatMap } from "@suse-o11y/flat-map";
import { intersection } from "@suse-o11y/intersection";
import { isEmpty } from "@suse-o11y/is-empty";
import { isPresent } from "@suse-o11y/is-present";
import { join } from "@suse-o11y/join";
import { map } from "@suse-o11y/map";
import { pipe } from "@suse-o11y/pipe";
import type { Record } from "@suse-o11y/record";
import { sort } from "@suse-o11y/sort";
import type { Template } from "@suse-o11y/template";
import { toEntries } from "@suse-o11y/to-entries";
import { toJSON } from "@suse-o11y/to-json";
import { toKeys } from "@suse-o11y/to-keys";
import { unique } from "@suse-o11y/unique";

export type Metric = Record<string, string>;

export type MetricTemplate = Template<["${", "}"], null>;

const PRIVATE_METRIC_NAME_REGEXP = /^__.+__$/u;

function isPublicLabelName(labelName: string): boolean {
	return !PRIVATE_METRIC_NAME_REGEXP.test(labelName);
}

function getPublicLabelNames(metric: Metric): Array<string> {
	return pipe(metric, toKeys(), filter(isPublicLabelName));
}

function toKey({ __name__, ...metric }: Metric): string {
	const payload = pipe(
		metric,
		toEntries(),
		filter(([field]) => isPublicLabelName(field)),
		sort(([left], [right]) => alphabeticalSorter(left, right)),
		map(([field, value]) => `${field}=${toJSON(value)}`),
		join(", "),
	);

	const key = pipe(
		[
			__name__,
			// should wrap only not empty payload around {}
			isEmpty(payload) ? null : `{${payload}}`,
		],
		filter(isPresent),
		join(" "),
	);

	return isEmpty(key) ? "result" : key;
}

function extractLabelValuesByName(labelName: string, metrics: ReadonlyArray<Metric>): Array<string> {
	const acc: Array<string> = [];

	for (const labels of metrics) {
		const value = labels[labelName] as undefined | string;

		if (value != null) {
			acc.push(value);
		}
	}

	return unique(acc);
}

function extractAllLabelNames(metrics: ReadonlyArray<Metric>): Array<string> {
	return pipe(metrics, flatMap(toKeys()), unique(), sort(alphabeticalSorter));
}

function extractIntersectingLabelNames(metrics: ReadonlyArray<Metric>): Array<string> {
	const [first = [], ...rest] = metrics.map(toKeys());

	return rest.reduce((acc, labels) => intersection(acc, labels), first);
}

function toLabel(labelTemplate: undefined | MetricTemplate, metric: Metric): string {
	return labelTemplate?.interpolate(metric) || toKey(metric);
}

function toComponentIdentifier(
	componentIdentifierTemplate: undefined | MetricTemplate,
	metric: Metric,
): undefined | string {
	const identifier = componentIdentifierTemplate?.interpolate(metric, {
		// componentIdentifier must have all templates resolved
		whenVariableMissing: undefined,
	});

	return identifier || undefined;
}

export const Metric = {
	toKey,
	toLabel,
	toComponentIdentifier,
	getPublicLabelNames,
	extractLabelValuesByName,
	extractAllLabelNames,
	extractIntersectingLabelNames,
};
